/**
 * GraphQL queries and hooks — techRadar pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type { TechRadarFilter, TechRadarResult, TechRadarTeamScope } from '../types/techRadar';

export const TECH_RADAR = gql`
  query TechRadar($filter: TechRadarFilter) {
    techRadar(filter: $filter) {
      sectors {
        label
        families
      }
      blips {
        id
        name
        ring
        sector
        family
        description
        supplier
        isNew
      }
    }
  }
`;

export function useTechRadar(
  filter?: TechRadarFilter,
  skip?: boolean
): QueryResult<{ techRadar: TechRadarResult }> {
  return useQuery(TECH_RADAR, {
    variables: { filter },
    skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export const TECH_RADAR_TEAM_SCOPE = gql`
  query TechRadarTeamScope($teamId: ID!) {
    techRadarTeamScope(teamId: $teamId) {
      teamCount
      serviceCount
      usage {
        technologyId
        serviceCount
      }
    }
  }
`;

/**
 * Technology usage across a team and its sub-teams (issue #2097). A separate query from
 * `techRadar`, with no id-keyed objects, so the page's blips are never refetched or re-cached.
 */
export function useTechRadarTeamScope(
  teamId: string,
  skip?: boolean
): QueryResult<{ techRadarTeamScope: TechRadarTeamScope }> {
  return useQuery(TECH_RADAR_TEAM_SCOPE, {
    variables: { teamId },
    skip: skip || !teamId,
  });
}

// ─── Search ────────────────────────────────────────────────────────────────
